import type { Translate } from "@/lib/i18n/translate";
import { Check, Headset } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { T } from "./demo-call-shared";

function bullets(t: Translate) {
  return [
    t("siehstLiveBildschirmHausverwalter"),
    t("gehenFragenZeigenAblaeufe"),
    t("gemeinsamSchauenHausverwalterAi"),
  ];
}

/** The dark "Dein Demo-Team / Das erwartet dich" reassurance panel.
 * `compact` = mobile sizing (smaller avatar + heading). */
export function DemoTeamPanel({
  compact = false,
}: {
  readonly compact?: boolean;
}): ReactNode {
  const t = useTranslations("public.demoCallPanel");
  const avatar = compact ? 54 : 62;
  return (
    <div
      style={{
        background: T.dark,
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        height: "100%",
        padding: compact ? "28px 22px 30px" : "52px 48px 56px",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          right: compact ? "-20%" : "-12%",
          top: compact ? "-16%" : "-18%",
          width: compact ? 280 : 380,
          height: compact ? 240 : 340,
          background:
            "radial-gradient(ellipse at center, rgba(255,255,255,0.10) 0%, transparent 66%)",
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: compact ? 13 : 15,
          }}
        >
          <span
            style={{
              width: avatar,
              height: avatar,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.12)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 10px 24px -8px rgba(0,0,0,0.5)",
            }}
          >
            <Headset size={compact ? 24 : 26} strokeWidth={1.7} color="#fff" />
          </span>
          <div>
            <div
              style={{
                fontSize: compact ? 17 : 19,
                fontWeight: 700,
                letterSpacing: "-0.01em",
              }}
            >
              {t("demoTeam")}
            </div>
            <div
              style={{
                fontSize: compact ? 12.5 : 13.5,
                color: "rgba(255,255,255,0.55)",
                marginTop: 2,
              }}
            >
              {t("produktexpertInnenHausverwalterAi")}
            </div>
          </div>
        </div>

        <div
          style={{
            height: 1,
            background: "rgba(255,255,255,0.12)",
            margin: compact ? "24px 0" : "28px 0",
          }}
        />

        <h2
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: compact ? 26 : 30,
            lineHeight: 1.12,
            letterSpacing: "-0.015em",
            margin: 0,
          }}
        >
          {t("erwartet")}
        </h2>
        <p
          style={{
            fontSize: compact ? 14 : 14.5,
            lineHeight: 1.6,
            color: "rgba(255,255,255,0.66)",
            margin: "14px 0 0",
          }}
        >
          {t("setzenZusammenStellenPlattform")}
        </p>

        <div
          style={{
            marginTop: compact ? 24 : 28,
            display: "flex",
            flexDirection: "column",
            gap: compact ? 18 : 20,
          }}
        >
          {bullets(t).map((text) => (
            <div
              key={text}
              style={{
                display: "flex",
                gap: compact ? 13 : 14,
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  width: compact ? 26 : 27,
                  height: compact ? 26 : 27,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.12)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                <Check size={14} strokeWidth={2.6} color="#5FC9B5" />
              </span>
              <span
                style={{
                  fontSize: compact ? 14 : 14.5,
                  lineHeight: 1.5,
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                {text}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
